import { supabase } from './supabase';

export const ELEMENT_KINDS = [
  'header',
  'footer',
  'upsell',
  'subscription',
  'cta',
  'other',
] as const;

export type ElementKind = (typeof ELEMENT_KINDS)[number];

export interface ContentElementRow {
  id: string;
  slug: string;
  name: string;
  kind: ElementKind;
  description: string | null;
  body_html: string;
  sort_order: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export function mapContentElementRow(raw: Record<string, unknown>): ContentElementRow {
  const kindRaw = String(raw.kind ?? 'other');
  const kind = ELEMENT_KINDS.includes(kindRaw as ElementKind)
    ? (kindRaw as ElementKind)
    : 'other';
  return {
    id: String(raw.id),
    slug: String(raw.slug ?? ''),
    name: String(raw.name ?? ''),
    kind,
    description: raw.description != null ? String(raw.description) : null,
    body_html: String(raw.body_html ?? ''),
    sort_order: Number(raw.sort_order ?? 0),
    active: Boolean(raw.active ?? true),
    created_at: String(raw.created_at ?? ''),
    updated_at: String(raw.updated_at ?? ''),
  };
}

export function sortContentElements(rows: ContentElementRow[]): ContentElementRow[] {
  return [...rows].sort((a, b) => {
    if (a.kind !== b.kind) return a.kind.localeCompare(b.kind);
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });
}

export function elementKindLabel(kind: ElementKind): string {
  const labels: Record<ElementKind, string> = {
    header: 'Header',
    footer: 'Footer',
    upsell: 'Upsell',
    subscription: 'Subscription',
    cta: 'CTA',
    other: 'Other',
  };
  return labels[kind] ?? kind;
}

export function elementSlugFromName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function isValidElementSlug(slug: string): boolean {
  return /^[a-z][a-z0-9_]*$/.test(slug) && slug.length >= 2 && slug.length <= 64;
}

export function filterActiveContentElements(rows: ContentElementRow[]): ContentElementRow[] {
  return rows.filter((row) => row.active);
}

export async function fetchActiveContentElements(): Promise<ContentElementRow[]> {
  const { data, error } = await supabase
    .from('elements')
    .select('*')
    .eq('active', true)
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });
  if (error) throw new Error(error.message);
  return filterActiveContentElements(
    sortContentElements((data ?? []).map((r) => mapContentElementRow(r as Record<string, unknown>))),
  );
}

export async function fetchAllContentElements(): Promise<ContentElementRow[]> {
  const { data, error } = await supabase
    .from('elements')
    .select('*')
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });
  if (error) throw new Error(error.message);
  return sortContentElements((data ?? []).map((r) => mapContentElementRow(r as Record<string, unknown>)));
}

export function contentElementsById(
  rows: ContentElementRow[],
): Map<string, ContentElementRow> {
  return new Map(rows.map((r) => [r.id, r]));
}

/** Collect element_id values from newsletter content JSON blocks. */
export function collectElementIdsFromContent(content: unknown): string[] {
  if (!Array.isArray(content)) return [];
  const ids = new Set<string>();
  for (const block of content) {
    if (!block || typeof block !== 'object' || Array.isArray(block)) continue;
    const b = block as Record<string, unknown>;
    const type = typeof b.type === 'string' ? b.type.toLowerCase() : '';
    if (type !== 'element') continue;
    const id = typeof b.element_id === 'string' ? b.element_id.trim() : '';
    if (id) ids.add(id);
  }
  return [...ids];
}

export function newElementSnapshotBlock(
  element: ContentElementRow,
): Record<string, unknown> {
  return {
    id: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Date.now()),
    type: 'html',
    content: element.body_html,
    meta: {
      source_element_id: element.id,
      source_element_slug: element.slug,
      snapshot: true,
    },
  };
}
